import * as Yup from "yup";
import AutoCompleteFormControl from "../../../components/formcontrols/AutoCompleteFormControl";
import ValidationErrorMessage from "../../../components/formcontrols/ValidationErrorMessage";
import { dateToyyyyMMdd } from "../../../util/date";
import { getPatchOperations } from "../../../util/form";
import IsRequired from "../../../components/formcontrols/common/IsRequired";
import { validationMessages } from "@/constants/validation";
import { PersonUdvalg } from "@/interfaces/schema";

export const personUdvalgForm = (
  queryValues: IFormControlGenericQueryValues,
  personId: string,
  udvalg?: PersonUdvalg,
): FormConfig<PersonUdvalgDTO> => {
  return {
    title: "udvalgspost",
    lockedValues: [
      {
        id: "personId",
        value: personId,
      },
      {
        id: "personUdvalgId",
        value: udvalg?.id,
      },
    ],
    validationSchema: Yup.object({
      udvalgId: Yup.string().required("Vælg et udvalg"),
      tillidshvervNr: Yup.string().required("Vælg et tillidshverv"),
      periodeFraDato: Yup.date().nullable().required("Startdato skal være sat"),
      periodeTilDatoInklusiv: Yup.date()
        .nullable()
        .transform((_currValue, originalValue) =>
          originalValue === null || originalValue === ""
            ? null
            : new Date(originalValue),
        )
        .test(
          "dateEndBeforeStart",
          validationMessages.dateEndBeforeStart,
          function () {
            const { periodeFraDato, periodeTilDatoInklusiv } = this.parent;
            if (!periodeTilDatoInklusiv) return true;
            return periodeTilDatoInklusiv >= periodeFraDato;
          },
        ),
    }),
    form: [
      {
        component: "custom",
        customComponent: ({ value, touched, error, onChange }) => (
          <>
            <label className="form-label">
              Udvalg <IsRequired />
            </label>
            <AutoCompleteFormControl
              className={touched && error ? "is-invalid" : ""}
              onChange={onChange}
              value={value}
              queryValues={queryValues}
            />
            <ValidationErrorMessage touched={touched} errorMessage={error} />
          </>
        ),
        id: "udvalgId",
        label: "Udvalg",
        initialValue: "",
        isRequired: true,
      },
      {
        component: "kode-select",
        kodeListeType: "TillidshvervKode",
        id: "tillidshvervNr",
        label: "Tillidshverv",
        placeholder: "Vælg tillidshvervtype",
        initialValue: "",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "periodeFraDato",
        label: "Startdato",
        initialValue: "",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "periodeTilDatoInklusiv",
        label: "Slutdato",
        initialValue: "",
      },
      {
        component: "textinput",
        id: "fratraadtAarsag",
        label: "Bemærkning",
        initialValue: "",
      },
    ],
    get: `person/${personId}/udvalg/${udvalg?.id}`,
    patch(values: PersonUdvalgDTO) {
      const ops = getPatchOperations(values);

      return {
        query: `person/${personId}/udvalg/${udvalg?.id}`,
        body: JSON.stringify(ops),
      };
    },
    post(values: PersonUdvalgDTO) {
      const {
        periodeFraDato,
        periodeTilDatoInklusiv,
        udvalgId,
        fratraadtAarsag,
        tillidshvervNr,
      } = values;
      return {
        query: `person/${personId}/udvalg/`,
        body: {
          udvalgId,
          periodeFraDato: dateToyyyyMMdd(periodeFraDato),
          periodeTilDatoInklusiv: periodeTilDatoInklusiv
            ? dateToyyyyMMdd(periodeTilDatoInklusiv)
            : "9999-12-31",
          fratraadtAarsag,
          tillidshvervNr: tillidshvervNr.toString(),
        },
      };
    },
    delete() {
      return {
        query: `person/${personId}/udvalg/${udvalg?.id}`,
      };
    },
  };
};
